import { useState, useEffect } from 'react';
import { supabase } from '@/lib/supabase';
import { usePosts } from '@/hooks/usePosts';

interface StatsData {
  mostViewed: { id: string; title: string; count: number }[];
  mostLiked: { id: string; title: string; count: number }[];
  mostHearted: { id: string; title: string; count: number }[];
}

function StatCard({ title, value, icon, colorClass }: { title: string; value: number; icon: string; colorClass: string }) {
  return (
    <div className="bg-background-50 rounded-lg border border-background-200 p-4 md:p-5">
      <div className="flex items-center gap-3 mb-2">
        <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${colorClass}`}>
          <i className={`${icon} text-lg`}></i>
        </div>
        <span className="text-2xl md:text-3xl font-bold text-foreground-950">{value}</span>
      </div>
      <span className="text-foreground-500 text-xs md:text-sm">{title}</span>
    </div>
  );
}

function TopList({ title, items, icon, colorClass }: {
  title: string;
  items: { id: string; title: string; count: number }[];
  icon: string;
  colorClass: string;
}) {
  if (items.length === 0) return null;

  return (
    <div className="bg-background-50 rounded-lg border border-background-200 p-4 md:p-5">
      <h3 className="font-heading font-bold text-foreground-950 text-base md:text-lg mb-4 flex items-center gap-2">
        <span className={`w-7 h-7 rounded-full flex items-center justify-center ${colorClass}`}>
          <i className={`${icon} text-sm`}></i>
        </span>
        {title}
      </h3>
      <div className="space-y-3">
        {items.map((item, i) => (
          <div key={item.id} className="flex items-center gap-3">
            <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0 ${
              i === 0 ? 'bg-primary-500 text-white' :
              i === 1 ? 'bg-primary-300 text-white' :
              i === 2 ? 'bg-primary-200 text-primary-700' :
              'bg-background-200 text-foreground-500'
            }`}>
              {i + 1}
            </span>
            <span className="text-sm text-foreground-700 flex-1 line-clamp-1">{item.title}</span>
            <span className="text-sm font-bold text-foreground-500 flex-shrink-0">{item.count}</span>
          </div>
        ))}
        {items.length === 0 && (
          <p className="text-sm text-foreground-400 py-2">Nenhum dado ainda.</p>
        )}
      </div>
    </div>
  );
}

export default function Dashboard() {
  const { posts } = usePosts();
  const [stats, setStats] = useState<StatsData>({
    mostViewed: [],
    mostLiked: [],
    mostHearted: [],
  });
  const [loading, setLoading] = useState(true);
  const [totalViews, setTotalViews] = useState(0);
  const [totalLikes, setTotalLikes] = useState(0);
  const [totalHearts, setTotalHearts] = useState(0);

  useEffect(() => {
    const fetchStats = async () => {
      setLoading(true);
      try {
        // Fetch views
        const { data: viewsData, error: viewsErr } = await supabase
          .from('post_views')
          .select('post_id');

        // Fetch reactions
        const { data: reactionsData, error: reactionsErr } = await supabase
          .from('post_reactions')
          .select('post_id, type');

        if (viewsErr) console.error(viewsErr);
        if (reactionsErr) console.error(reactionsErr);

        const postMap = new Map(posts.map(p => [p.id, p.title]));

        // Count views per post
        const viewCounts: Record<string, number> = {};
        (viewsData ?? []).forEach(v => {
          viewCounts[v.post_id] = (viewCounts[v.post_id] || 0) + 1;
        });
        setTotalViews((viewsData ?? []).length);

        // Count likes and hearts per post
        const likeCounts: Record<string, number> = {};
        const heartCounts: Record<string, number> = {};
        (reactionsData ?? []).forEach(r => {
          if (r.type === 'like') {
            likeCounts[r.post_id] = (likeCounts[r.post_id] || 0) + 1;
          } else {
            heartCounts[r.post_id] = (heartCounts[r.post_id] || 0) + 1;
          }
        });
        setTotalLikes(Object.values(likeCounts).reduce((a, b) => a + b, 0));
        setTotalHearts(Object.values(heartCounts).reduce((a, b) => a + b, 0));

        const topN = <T extends Record<string, number>>(counts: T, n: number) =>
          Object.entries(counts)
            .sort(([, a], [, b]) => b - a)
            .slice(0, n)
            .map(([id, count]) => ({ id, title: postMap.get(id) ?? 'Post não encontrado', count }));

        setStats({
          mostViewed: topN(viewCounts, 10),
          mostLiked: topN(likeCounts, 10),
          mostHearted: topN(heartCounts, 10),
        });
      } catch {
        // silent
      } finally {
        setLoading(false);
      }
    };

    if (posts.length > 0) {
      fetchStats();
    }
  }, [posts]);

  if (loading) {
    return (
      <div className="text-center py-12">
        <i className="ri-loader-4-line animate-spin text-primary-500 text-3xl"></i>
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h2 className="font-heading font-bold text-foreground-950 text-xl md:text-2xl">
          Dashboard
        </h2>
        <span className="text-foreground-400 text-sm">{posts.length} artigos publicados</span>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 md:gap-4 mb-6 md:mb-8">
        <StatCard
          title="Visualizações totais"
          value={totalViews}
          icon="ri-eye-line"
          colorClass="bg-blue-50 text-blue-500"
        />
        <StatCard
          title="Curtidas"
          value={totalLikes}
          icon="ri-thumb-up-line"
          colorClass="bg-primary-50 text-primary-500"
        />
        <StatCard
          title="Corações"
          value={totalHearts}
          icon="ri-heart-line"
          colorClass="bg-red-50 text-red-500"
        />
      </div>

      {/* Top lists */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
        <TopList
          title="Mais visualizados"
          items={stats.mostViewed}
          icon="ri-eye-line"
          colorClass="bg-blue-50 text-blue-500"
        />
        <TopList
          title="Mais curtidos"
          items={stats.mostLiked}
          icon="ri-thumb-up-line"
          colorClass="bg-primary-50 text-primary-500"
        />
        <TopList
          title="Mais corações"
          items={stats.mostHearted}
          icon="ri-heart-line"
          colorClass="bg-red-50 text-red-500"
        />
      </div>
    </div>
  );
}